import React from "react";
import { classnames, Icon } from "@culturehq/components";
import { t, Trans } from "app/utils";

type PageOnClick = (page: number) => void;
type PageLinkProps = {
  ariaLabel: string;
  smallCircle?: boolean;
  disabled?: boolean;
  navButton?: boolean;
  children: React.ReactNode;
  current?: boolean;
  onClick: PageOnClick;
  page: number;
};

const PageLink: React.FC<PageLinkProps> = ({
  ariaLabel, children, current, disabled, onClick, page, smallCircle, navButton
}) => {
  const onButtonClick = () => {
    if (!current) {
      onClick(page);
    }
  };

  return (
    <button
      aria-label={ariaLabel}
      type="button"
      disabled={disabled}
      aria-current={current}
      className={
        classnames("chq-pag--bn", smallCircle ? "chq-pag--bn-small" : "", navButton ? "chq-pag--bn-nav" : "")
      }
      onClick={onButtonClick}
      tabIndex={0}
    >
      {children}
    </button>
  );
};

type PageProps = Omit<PageLinkProps, "children">;

const Page: React.FC<PageProps> = ({ children, page, ...props }) => (
  <PageLink page={page} {...props}>
    {children}
  </PageLink>
);

type PrevPageProps = {
  children: React.ReactNode;
  currentPage: number;
  onClick: PageOnClick;
};

const PrevPage: React.FC<PrevPageProps> = ({ children, currentPage, onClick }) => (
  <PageLink
    ariaLabel={t("Previous page")}
    disabled={currentPage === 1}
    navButton
    onClick={onClick}
    page={currentPage - 1}
  >
    {children}
  </PageLink>
);

type NextPageProps = {
  children: React.ReactNode;
  currentPage: number;
  onClick: PageOnClick;
  totalPages: number;
};

const NextPage: React.FC<NextPageProps> = ({ children, currentPage, totalPages, onClick }) => (
  <PageLink
    ariaLabel={t("Next page")}
    disabled={currentPage === totalPages}
    navButton
    onClick={onClick}
    page={currentPage + 1}
  >
    {children}
  </PageLink>
);

type PaginationProps = {
  className?: string;
  currentPage: number;
  onClick: PageOnClick;
  totalPages: number;
  smallVersion?: boolean;
};

const IconsPagination: React.FC<PaginationProps> = ({
  className,
  currentPage = 1,
  totalPages,
  onClick,
  smallVersion = false
}) => {
  if (totalPages < 2) {
    return null;
  }

  const innerWindow = (
    [...Array(3)].map((_, index) => index + currentPage - 1)
      .filter(page => page >= 1 && page <= totalPages)
  );

  return (
    <nav className={classnames("chq-pag", "chq-icon-pag", className)}>
      <PrevPage currentPage={currentPage} onClick={onClick}>
        <Icon icon="chevron-left" />
        {!smallVersion && <Trans>Back</Trans>}
      </PrevPage>
      <div className="flx-row">
        {currentPage > 2 && (
          <Page
            ariaLabel={t("First page")}
            current={currentPage === 1}
            onClick={onClick}
            page={1}
            smallCircle
          />
        )}
        {innerWindow.map(page => (
          <Page ariaLabel={t(`Page ${page}`)} key={page} page={page} current={page === currentPage} onClick={onClick} />
        ))}
        {currentPage < totalPages - 1 && (
          <Page
            ariaLabel={t("Last page")}
            current={currentPage === totalPages}
            onClick={onClick}
            page={totalPages}
            smallCircle
          />
        )}
      </div>
      <NextPage currentPage={currentPage} totalPages={totalPages} onClick={onClick}>
        {!smallVersion && <Trans>Next</Trans>}
        <Icon icon="chevron-right" />
      </NextPage>
    </nav>
  );
};

export default IconsPagination;
